import { api } from "./client";

export interface FlightDetail {
  id: number;
  replay_id: string | null;
  title: string | null;
  session_name: string | null;
  mission_name: string | null;
  map_name: string | null;
  scenario_type: string | null;
  status: string;
  start_time: string | null;
  end_time: string | null;
  duration_seconds: number | null;
  weather_summary?: Record<string, unknown> | null;
  structures?: Array<Record<string, unknown>>;
  entities?: Array<Record<string, unknown>>;
  primary_entity_source_id?: number | null;
}

export interface FlightMetrics {
  sample_count: number;
  duration_seconds: number;
  telemetry_rate_hz: number;
  total_distance_m: number;
  min_speed_mps: number | null;
  avg_speed_mps: number | null;
  max_speed_mps: number | null;
  min_altitude_m: number | null;
  avg_altitude_m: number | null;
  max_altitude_m: number | null;
  min_vertical_rate_mps: number | null;
  avg_vertical_rate_mps: number | null;
  max_vertical_rate_mps: number | null;
  total_climb_m: number;
  total_descent_m: number;
  heading_change_deg: number;
  max_turn_rate_deg_s: number | null;
  max_acceleration_mps2: number | null;
  max_gap_seconds: number | null;
  hover_ratio: number | null;
  smoothness_score: number | null;
}

export interface ReplayPoint {
  elapsed_seconds: number;
  timestamp_ms: number;
  latitude: number;
  longitude: number;
  altitude_m: number | null;
  speed_mps: number | null;
  yaw_deg: number | null;
  pitch_deg: number | null;
  roll_deg: number | null;
  fuel: number | null;
  fuel_capacity: number | null;
}

export interface WeatherSample {
  elapsed_seconds: number;
  observed_at: string | null;
  scenario_time_utc: string | null;
  scenario_time_local: string | null;
  fog_density: number | null;
  fog_base_altitude_m: number | null;
  overcast_coverage_pct: number | null;
  overcast_density: number | null;
  precipitation_type: string | null;
  precipitation_density: number | null;
  precipitation_severity: number | null;
  wind_horizontal_speed_mps: number | null;
  wind_direction_deg: number | null;
  air_temperature_kelvin: number | null;
  local_air_temperature_kelvin: number | null;
  air_pressure_hpa: number | null;
}

export interface ReplayResponse {
  flight_id: number;
  entity_id: number;
  points: ReplayPoint[];
  weather_samples?: WeatherSample[];
}

export interface ChartSample {
  elapsed_seconds: number;
  altitude_m: number | null;
  speed_mps: number | null;
  vertical_rate_mps: number | null;
  yaw_deg: number | null;
  [key: string]: number | string | boolean | null;
}

export interface ChartSeriesResponse {
  flight_id: number;
  entity_id: number;
  samples: ChartSample[];
}

export interface FlightLapSector {
  sector_index: number;
  from_circle_sequence: number;
  to_circle_sequence: number;
  start_elapsed_seconds: number;
  end_elapsed_seconds: number;
  duration_seconds: number;
}

export interface FlightLapCircle {
  sequence_index: number;
  latitude: number;
  longitude: number;
  altitude_m: number;
  source_structure_id: number | null;
  elapsed_seconds: number;
  observed_at: string | null;
}

export interface FlightLap {
  id: number;
  lap_index: number;
  is_complete: boolean;
  checkpoint_count: number;
  circle_total: number | null;
  start_elapsed_seconds: number;
  end_elapsed_seconds: number;
  lap_time_seconds: number;
  start_observed_at: string | null;
  end_observed_at: string | null;
  is_best: boolean;
  circles: FlightLapCircle[];
  sectors: FlightLapSector[];
}

export interface FlightLapsResponse {
  flight_id: number;
  laps: FlightLap[];
}

export interface FlightUserInput {
  id: number;
  elapsed_seconds: number;
  timestamp_ms: number;
  observed_at: string | null;
  input_type: string;
  device_name: string | null;
  device_identifier: string | null;
  display_name: string | null;
  match_quality: string | null;
  action_name: string | null;
  action_value: number | null;
  is_active: boolean | null;
  key_code: number | null;
  key_name: string | null;
  axis_lx: number | null;
  axis_ly: number | null;
  axis_rx: number | null;
  axis_ry: number | null;
  axis_slider_1: number | null;
  axis_slider_2: number | null;
  axis_slider_3: number | null;
  axis_slider_4: number | null;
  buttons: Record<string, unknown> | null;
  event_category: string | null;
  event_name: string | null;
}

export interface FlightUserInputsResponse {
  flight_id: number;
  user_inputs: FlightUserInput[];
}

export interface FlightAiFeedback {
  flight_id: number;
  model: string | null;
  generated_at: string | null;
  status: string | null;
  strengths?: string[];
  areas_to_improve?: string[];
  summary?: string | null;
  [key: string]: unknown;
}

export function getFlightDetail(id: number) {
  return api.get<FlightDetail>(`/api/flights/${id}`);
}

export function getFlightMetrics(id: number) {
  return api.get<FlightMetrics>(`/api/flights/${id}/metrics`);
}

export function getFlightReplay2d(id: number) {
  return api.get<ReplayResponse>(`/api/flights/${id}/replay-2d`);
}

export function getFlightCharts(id: number) {
  return api.get<ChartSeriesResponse>(`/api/flights/${id}/charts`);
}

export function getFlightAiFeedback(id: number) {
  return api.get<FlightAiFeedback>(`/api/flights/${id}/ai-feedback`);
}

export function generateFlightAiFeedback(id: number) {
  return api.post<FlightAiFeedback>(`/api/flights/${id}/ai-feedback`);
}

export function getFlightLaps(id: number) {
  return api.get<FlightLapsResponse>(`/api/flights/${id}/laps`);
}

export function getFlightUserInputs(id: number) {
  return api.get<FlightUserInputsResponse>(`/api/flights/${id}/user-inputs`);
}
